import React, { useState, useEffect } from 'react';
import { Send, Ban, Bot, User } from 'lucide-react';
import { dashboardApi } from '../services/api';

/**
 * Module 6 — the conversation thread for one influencer.
 *
 * Chronological, both directions, with the AI classification (category,
 * confidence, reason, model) shown against the message it applies to, plus
 * which mailbox sent each outbound message. Drop into InfluencerProfile:
 *
 *   <ConversationThread influencerId={id} />
 */

function statusClass(status) {
  switch (status) {
    case 'booked': return 'bg-green-100 text-green-800';
    case 'interested': return 'bg-emerald-100 text-emerald-800';
    case 'replied': return 'bg-blue-100 text-blue-800';
    case 'contacted': return 'bg-yellow-100 text-yellow-800';
    case 'bounced': return 'bg-orange-100 text-orange-800';
    case 'unsubscribed':
    case 'not interested': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
}

const ConversationThread = ({ influencerId }) => {
  const [data, setData] = useState(null);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const res = await dashboardApi.getThread(influencerId);
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  useEffect(() => { if (influencerId) load(); }, [influencerId]);

  const send = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await dashboardApi.sendManualReply(influencerId, { body: reply });
      setReply('');
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  const suppress = async () => {
    setBusy(true);
    try {
      await dashboardApi.suppressInfluencer(influencerId, { notes: 'suppressed from thread view' });
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return <div className="p-4 text-gray-500">{error || 'Loading conversation…'}</div>;
  }

  const { influencer, messages } = data;

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Conversation</h2>
          <p className="text-sm text-gray-500">
            {messages.length} message{messages.length === 1 ? '' : 's'}
            {influencer.instantly_lead_id && (
              <span className="text-gray-400"> · lead {influencer.instantly_lead_id.slice(0, 8)}…</span>
            )}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusClass(influencer.status)}`}>
            {influencer.status}
          </span>
          {influencer.unsubscribed === 1 && (
            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
              suppressed
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="px-6 py-4 space-y-4 max-h-[32rem] overflow-y-auto">
        {messages.length === 0 && (
          <p className="text-sm text-gray-500">No messages yet.</p>
        )}

        {messages.map((m) => {
          const inbound = m.direction === 'inbound';
          return (
            <div key={m.id} className={`flex ${inbound ? 'justify-start' : 'justify-end'}`}>
              <div className={`max-w-2xl w-full rounded-lg border p-4 ${
                inbound ? 'bg-gray-50 border-gray-200' : 'bg-blue-50 border-blue-200'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center text-xs font-medium text-gray-600">
                    {inbound
                      ? <><User className="w-3.5 h-3.5 mr-1.5" />@{influencer.username}</>
                      : <><Send className="w-3.5 h-3.5 mr-1.5" />Us{m.mailbox ? ` · ${m.mailbox}` : ''}</>}
                  </div>
                  <span className="text-xs text-gray-400">
                    {m.occurred_at ? new Date(m.occurred_at).toLocaleString() : ''}
                  </span>
                </div>

                {m.subject && (
                  <p className="text-sm font-medium text-gray-800 mb-1">{m.subject}</p>
                )}
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{m.body}</p>

                {/* AI decision shown against the message it applies to. */}
                {m.category && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex items-center flex-wrap gap-2 text-xs">
                      <Bot className="w-3.5 h-3.5 text-gray-400" />
                      <span className="px-2 py-0.5 rounded-full bg-white border border-gray-300 font-medium">
                        {m.overridden_category || m.category}
                      </span>
                      {m.overridden_category && (
                        <span className="text-purple-700">
                          overridden from <em>{m.category}</em>
                          {m.overridden_by ? ` by ${m.overridden_by}` : ''}
                        </span>
                      )}
                      {m.confidence != null && (
                        <span className="text-gray-500">{(m.confidence * 100).toFixed(0)}% confident</span>
                      )}
                      {m.model && <span className="text-gray-400">{m.model}</span>}
                      {m.needs_review === 1 && (
                        <span className="px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800">
                          awaiting review
                        </span>
                      )}
                    </div>
                    {m.reason && <p className="mt-1 text-xs text-gray-600">{m.reason}</p>}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="px-6 py-4 border-t border-gray-200">
        <textarea
          className="w-full border border-gray-300 rounded-lg p-3 text-sm"
          rows={4}
          placeholder="Write a manual reply…"
          value={reply}
          onChange={(e) => setReply(e.target.value)}
        />
        <div className="mt-2 flex items-center justify-between">
          <button
            onClick={suppress}
            disabled={busy}
            className="flex items-center px-3 py-1.5 text-sm text-red-700 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
          >
            <Ban className="w-4 h-4 mr-1.5" /> Suppress permanently
          </button>
          <button
            onClick={send}
            disabled={busy || !reply.trim()}
            className="flex items-center px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <Send className="w-4 h-4 mr-1.5" /> Send reply
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConversationThread;
